import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import GoogleLoginModal from './GoogleLoginModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import { userService } from '../services/userService';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-white);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: var(--color-white);
  border: 2px solid var(--color-light-gray);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--color-black);
  text-align: center;
  margin-bottom: 10px;
  letter-spacing: 1px;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--color-gray);
  text-align: center;
  margin-bottom: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-black);
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid var(--color-light-gray);
  font-size: 1rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-blue);
  }
`;

const Button = styled.button<{ $primary?: boolean; $disabled?: boolean }>`
  background-color: ${props => {
    if (props.$disabled) return '#ccc';
    return props.$primary ? 'var(--color-black)' : 'transparent';
  }};
  color: ${props => props.$primary ? 'var(--color-white)' : 'var(--color-blue)'};
  border: ${props => props.$primary ? 'none' : '2px solid var(--color-blue)'};
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.$primary ? 'var(--color-blue)' : 'var(--color-blue)'};
    color: var(--color-white);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.3);
  }
`;

const GoogleButton = styled.button`
  background-color: var(--color-black);
  color: var(--color-white);
  border: none;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  
  &:hover {
    background-color: var(--color-blue);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.3);
  }
`;

const GoogleText = styled.span`
  font-weight: 700;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: var(--color-light-gray);
  }
`;

const DividerText = styled.span`
  padding: 0 20px;
  color: var(--color-gray);
  font-size: 0.9rem;
`;

const RegisterLink = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 0.9rem;
  color: var(--color-gray);
`;

const LinkStyled = styled(Link)`
  color: var(--color-blue);
  text-decoration: none;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ForgotPasswordLink = styled.span`
  color: var(--color-blue);
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  display: inline-block;
  margin-top: -10px;
  margin-bottom: 10px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 12px;
  font-size: 0.9rem;
  text-align: center;
`;

interface LoginScreenProps {
  onLogin: (userData?: any) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // 使用 Firebase 登录
      await userService.login(email, password);
      onLogin();
      navigate('/');
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error.message || 'Login failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = (userData: any) => {
    console.log('Google登录成功:', userData);
    onLogin(userData);
  };

  return (
    <Container>
      <LoginCard>
        <Logo>ACCORD</Logo>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
            <ForgotPasswordLink onClick={() => setShowForgotPasswordModal(true)}>
              Forgot password?
            </ForgotPasswordLink>
          </InputGroup>

          <Button type="submit" $primary disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>
        </Form>

        <Divider>
          <DividerText>or</DividerText>
        </Divider>

        <GoogleButton onClick={() => setShowGoogleModal(true)}>
          Login with <GoogleText>
            <span style={{ color: '#4285F4' }}>G</span>
            <span style={{ color: '#EA4335' }}>O</span>
            <span style={{ color: '#FBBC05' }}>O</span>
            <span style={{ color: '#4285F4' }}>G</span>
            <span style={{ color: '#34A853' }}>L</span>
            <span style={{ color: '#EA4335' }}>E</span>
          </GoogleText>
        </GoogleButton>

        <RegisterLink>
          Don't have an account? <LinkStyled to="/register">Sign up</LinkStyled>
        </RegisterLink>
      </LoginCard>

      <GoogleLoginModal
        visible={showGoogleModal}
        onClose={() => setShowGoogleModal(false)}
        onSuccess={handleGoogleLogin}
      />

      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
        onSuccess={() => {
          setShowForgotPasswordModal(false);
        }}
      />
    </Container>
  );
};

export default LoginScreen;
